// 001 — Admisión-Alta-Egreso (Módulo H, Grupo 3): puerto a hce-chart de
// packages/hce/src/hce/forms/Formulario001.tsx. A diferencia de los Grupos
// 1/2, no crea un QuestionnaireResponse — edita un Encounter existente
// (status/class/period/hospitalization) vía medplum.updateResource. Sin
// Questionnaire ni PDF asociado en el original; el punto 11 del pedido
// ("todo documento debe llevar firma e impresión") es funcionalidad nueva
// acá — al guardar arma un PDF en modo `pares` (pdfFormularioGenerico.ts)
// con los campos recién guardados y ofrece el mismo PanelFirmaImpresion que
// el resto del catálogo.
//
// Además del Encounter, trae del papel (páginas 4-5 del PDF, spec en docs/hce-anexos/001-admision-alta-egreso.md) el nombre del
// admisionista (B), el registro de cambios (E) y el egreso hospitalario (F, 6 órdenes); ver `discretos/bloques001Datos.ts`. El PUT del
// Encounter y las Condition de los diagnósticos de egreso van en UNA transacción.
//
// Es a la vez la página de ruta (lee :id/?encounter=, aplica el gate de
// especialidad) y el formulario — mismo patrón que
// MotorFormularioConsentimiento.tsx (Grupo 2). Sin encounter en la URL no
// tiene sentido (edita uno existente, no crea uno) — SelectorFormularios.tsx
// ya oculta el link en ese caso; acá se muestra un mensaje en vez de un
// formulario roto, por si se llega por URL directa.
import type { Encounter, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { evaluarCompletitudDeEncounter } from '@/paciente/chequeoCompletitudConsulta';
import { camposFiliacionFaltantes } from '@/paciente/filiacionCompleta';
import type { PatientChartOutletContext } from '@/paciente/PatientChartPage';
import { PanelFirmaImpresion } from './PanelFirmaImpresion';
import { Bloque, CamposSimples } from './discretos/camposSimples';
import { ErrorGuardado } from './discretos/ErrorGuardado';
import { useGuardado } from './discretos/useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from './EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from './useDatosEncabezadoMsp';
import { SelectorDiagnosticoConMarca } from './discretos/MarcaDiagnostico';
import { encounterCerrado, idTemporal, LoteTransaccional, recursoRespuesta } from './discretos/transaccionFhir';
import {
  CAMPOS_ADMISION_001,
  CAMPOS_CAMBIO_001,
  CAMPOS_ORDEN_001,
  conditionDeEgreso001,
  diagnosisDeEncounter001,
  diagnosticosDeEgreso001,
  estadoInicial001,
  MARCAS_001,
  paresDe001,
  yaTieneDiagnosticosDeEgreso,
  type Estado001,
} from './discretos/bloques001Datos';

const CODIGO = '001';

type EstadoEncounter = NonNullable<Encounter['status']>;

const ESTADOS: EstadoEncounter[] = ['planned', 'arrived', 'triaged', 'in-progress', 'onleave', 'finished', 'cancelled'];
const CLASES = [
  { value: 'AMB', label: 'Ambulatorio' },
  { value: 'IMP', label: 'Hospitalización' },
  { value: 'EMER', label: 'Emergencia' },
  { value: 'ACUTE', label: 'Cuidado agudo' },
];

type Fase = { fase: 'cargando' } | { fase: 'error'; mensaje: string } | { fase: 'formulario' } | { fase: 'guardado' };
type Par = { pregunta: string; respuesta: string };

export function Formulario001Page(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const encounterParam = searchParams.get('encounter');
  const especialidad = useEspecialidadDelPracticante();

  const subject = useMemo<Reference<Patient>>(() => ({ reference: `Patient/${id}` }), [id]);
  const encounter = useMemo<Reference<Encounter> | undefined>(() => (encounterParam ? { reference: encounterParam } : undefined), [encounterParam]);
  // Filiación (Fase 2, Anexo 1 MSP): el 001 es el documento legal de admisión, bloquea
  // la firma si falta algún campo obligatorio de Datos Demográficos — no solo avisa,
  // a diferencia de faltaEpicrisis abajo. Ver camposFiliacionFaltantes.
  const patient = useResource<Patient>(subject);
  const faltantesFiliacion = patient ? camposFiliacionFaltantes(patient) : [];

  const [fase, setFase] = useState<Fase>({ fase: 'cargando' });
  const [actual, setActual] = useState<Encounter>();
  const [status, setStatus] = useState<EstadoEncounter>('planned');
  const [clase, setClase] = useState('AMB');
  const [inicio, setInicio] = useState('');
  const [fin, setFin] = useState('');
  const [admitSource, setAdmitSource] = useState('');
  const [dischargeDisposition, setDischargeDisposition] = useState('');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [paresGuardados, setParesGuardados] = useState<Par[]>([]);
  const [datos001, setDatos001] = useState<Estado001>(() => estadoInicial001());
  // Bloque A del papel (institución, unicódigo, establecimiento, HCU, archivo, datos del paciente) y PDF con encabezado MSP.
  const [camposMsp, setCamposMsp] = useState(() => valoresInicialesEncabezadoMsp());
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, camposMsp, CODIGO, t('formularios.f001.titulo'));
  const { guardando, error: errorGuardado, guardar: ejecutarGuardado } = useGuardado();
  const [numGuardados, setNumGuardados] = useState(0);
  // Descartar (pedido explícito de Sue 2026-09-27): SOLO existe antes de
  // guardar (numGuardados === 0) — una vez guardado, la única salida es
  // firmar (ver `pendienteFirma`/bloqueo de salida más abajo).
  const [descartado, setDescartado] = useState(false);
  const [firmado, setFirmado] = useState(false);
  const reportarPendienteFirma = useOutletContext<PatientChartOutletContext | undefined>()?.reportarPendienteFirma;
  const pendienteFirma = fase.fase === 'guardado' && !firmado && !descartado;
  useEffect(() => {
    reportarPendienteFirma?.(pendienteFirma);
  }, [reportarPendienteFirma, pendienteFirma]);
  useEffect(() => () => reportarPendienteFirma?.(false), [reportarPendienteFirma]);
  // Falta epicrisis al momento MISMO de dar el alta (2026-09-10) — no
  // esperar a que el médico salga de la ficha (AlertaCompletitudPopup.tsx
  // también lo detectaría ahí, pero solo si de verdad llega a salir; acá se
  // avisa apenas se guarda IMP+finished, que es el momento en que el médico
  // ya está pensando en cerrar el caso). `null` = no aplica (no es alta de
  // internación) o no se pudo determinar todavía.
  const [faltaEpicrisis, setFaltaEpicrisis] = useState(false);

  useEffect(() => {
    if (!encounter) return;
    let cancelado = false;
    medplum
      .readReference(encounter)
      .then((e) => {
        if (cancelado) return;
        // Hallazgo del revisor, 2026-09-14/16: sin este chequeo, un
        // `?encounter=` de la URL apuntando a OTRO paciente dejaba editar
        // (admisión/alta, incluido el estado clínico) el Encounter ajeno
        // bajo la ficha de este paciente. Fail-closed: se trata igual que
        // "no hay Encounter" — ver enlaceEncounter.ts / useEncounterValidado.ts.
        if (e.subject?.reference !== subject.reference) {
          setFase({ fase: 'error', mensaje: t('formularios.f001.encounterAjeno') });
          return;
        }
        setActual(e);
        setStatus(e.status ?? 'planned');
        setClase(e.class?.code ?? 'AMB');
        setInicio(e.period?.start?.slice(0, 16) ?? '');
        setFin(e.period?.end?.slice(0, 16) ?? '');
        setAdmitSource(e.hospitalization?.admitSource?.text ?? '');
        setDischargeDisposition(e.hospitalization?.dischargeDisposition?.text ?? '');
        setOrigin(e.hospitalization?.origin?.display ?? '');
        setDestination(e.hospitalization?.destination?.display ?? '');
        setFase({ fase: 'formulario' });
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        setFase({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, encounter, subject.reference, t]);

  // D-052: una atención terminada (`finished`), cancelada o anulada ya no se edita; solo se anula (entered-in-error). El servidor lo impone; aquí se avisa antes de enviar.
  const atencionCerrada = encounterCerrado(actual);

  async function guardarEncounter(): Promise<void> {
    if (!actual || encounterCerrado(actual)) return;
    const ahoraIso = new Date().toISOString();
    const lote = new LoteTransaccional();
    // Diagnósticos de egreso: una Condition cada uno, enlazada al Encounter por urn:uuid en la misma transacción; solo la primera vez.
    const nuevos = yaTieneDiagnosticosDeEgreso(actual) ? [] : diagnosticosDeEgreso001(datos001);
    const urls = nuevos.map(() => idTemporal());
    const encounterLocal: Encounter & { id: string } = {
      ...(actual as Encounter & { id: string }),
      status,
      class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: clase },
      period: { start: inicio ? new Date(inicio).toISOString() : undefined, end: fin ? new Date(fin).toISOString() : undefined },
      hospitalization: {
        admitSource: admitSource ? { text: admitSource } : undefined,
        dischargeDisposition: dischargeDisposition ? { text: dischargeDisposition } : undefined,
        origin: origin ? { display: origin } : undefined,
        destination: destination ? { display: destination } : undefined,
      },
      ...(nuevos.length > 0 ? { diagnosis: [...(actual.diagnosis ?? []), ...nuevos.map((_, i) => diagnosisDeEncounter001(urls[i]!, i + 1))] } : {}),
    };
    lote.actualizar(encounterLocal);
    nuevos.forEach((d, i) => lote.crear(conditionDeEgreso001(d, subject, { reference: `Encounter/${encounterLocal.id}` }, ahoraIso), urls[i]));
    const respuesta = await lote.enviar(medplum);
    const actualizado = recursoRespuesta(respuesta, 0, encounterLocal);
    setActual(actualizado);
    setParesGuardados([
      { pregunta: t('formularios.f001.estado'), respuesta: t(`formularios.f001.estados.${status}`) },
      { pregunta: t('formularios.f001.clase'), respuesta: CLASES.find((c) => c.value === clase)?.label ?? clase },
      ...(inicio ? [{ pregunta: t('formularios.f001.fechaIngreso'), respuesta: inicio }] : []),
      ...(fin ? [{ pregunta: t('formularios.f001.fechaEgreso'), respuesta: fin }] : []),
      ...(admitSource ? [{ pregunta: t('formularios.f001.origenAdmision'), respuesta: admitSource }] : []),
      ...(dischargeDisposition ? [{ pregunta: t('formularios.f001.destinoEgreso'), respuesta: dischargeDisposition }] : []),
      ...(origin ? [{ pregunta: t('formularios.f001.procedencia'), respuesta: origin }] : []),
      ...(destination ? [{ pregunta: t('formularios.f001.destino'), respuesta: destination }] : []),
      ...paresDe001(datos001),
    ]);
    setNumGuardados((n) => n + 1);
    setFase({ fase: 'guardado' });

    const hallazgos = await evaluarCompletitudDeEncounter(medplum, id, actualizado);
    setFaltaEpicrisis(hallazgos.some((h) => h.clave === 'epicrisis-alta'));
  }

  if (especialidad.cargando) {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  // Fail-closed cuando la especialidad no resolvió — ver el comentario largo
  // en FormularioDiscretoPage.tsx (mismo bug real, mismo fix, en los 5
  // gates del catálogo).
  if (!especialidad.codigo || !codigosFormularioDeEspecialidad(especialidad.codigo).includes(CODIGO)) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.fueraDeEspecialidadTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.fueraDeEspecialidadDetalle')}</AlertDescription>
      </Alert>
    );
  }

  if (!encounter) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formularios.f001.titulo')}</AlertTitle>
        <AlertDescription>{t('formularios.f001.faltaEncounter')}</AlertDescription>
      </Alert>
    );
  }

  if (fase.fase === 'cargando') {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  if (fase.fase === 'error') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.errorTitulo')}</AlertTitle>
        <AlertDescription>{fase.mensaje}</AlertDescription>
      </Alert>
    );
  }

  if (descartado) {
    return (
      <Alert>
        <AlertTitle>{t('formulario.descartadoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.descartadoDetalle')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-medium text-hce-ink">{t('formularios.f001.titulo')}</h3>
        {numGuardados === 0 && (
          <Button type="button" variant="outline" size="sm" onClick={() => setDescartado(true)}>
            {t('formulario.descartar')}
          </Button>
        )}
      </div>

      <EncabezadoMspCampos subject={subject} value={camposMsp} onChange={setCamposMsp} />

      <div className="flex max-w-md flex-col gap-4">

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-estado">{t('formularios.f001.estado')}</Label>
        <Select value={status} onValueChange={(v) => setStatus(v as EstadoEncounter)}>
          <SelectTrigger id="f001-estado">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ESTADOS.map((e) => (
              <SelectItem key={e} value={e}>
                {t(`formularios.f001.estados.${e}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-clase">{t('formularios.f001.clase')}</Label>
        <Select value={clase} onValueChange={setClase}>
          <SelectTrigger id="f001-clase">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CLASES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-inicio">{t('formularios.f001.fechaIngreso')}</Label>
        <Input id="f001-inicio" type="datetime-local" value={inicio} onChange={(e) => setInicio(e.target.value)} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-fin">{t('formularios.f001.fechaEgreso')}</Label>
        <Input id="f001-fin" type="datetime-local" value={fin} onChange={(e) => setFin(e.target.value)} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-admit-source">{t('formularios.f001.origenAdmision')}</Label>
        <Input id="f001-admit-source" value={admitSource} onChange={(e) => setAdmitSource(e.target.value)} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-discharge-disposition">{t('formularios.f001.destinoEgreso')}</Label>
        <Input id="f001-discharge-disposition" value={dischargeDisposition} onChange={(e) => setDischargeDisposition(e.target.value)} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-origin">{t('formularios.f001.procedencia')}</Label>
        <Input id="f001-origin" value={origin} onChange={(e) => setOrigin(e.target.value)} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="f001-destination">{t('formularios.f001.destino')}</Label>
        <Input id="f001-destination" value={destination} onChange={(e) => setDestination(e.target.value)} />
      </div>
      </div>

      <Bloque titulo={t('formularios.f001.admisionTitulo')}>
        <CamposSimples prefijoId="f001-adm" campos={CAMPOS_ADMISION_001} valores={datos001.admision} columnas={2} onChange={(v) => setDatos001((prev) => ({ ...prev, admision: v }))} />
      </Bloque>

      <Bloque titulo={t('formularios.f001.cambiosTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f001.cambiosNota')}</p>
        {datos001.cambios.map((c, i) => (
          <details key={i} open={i === 0} className="rounded-md border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium text-hce-ink">{`${t('formularios.f001.cambioRotulo')} ${i + 1}`}</summary>
            <div className="mt-3">
              <CamposSimples
                prefijoId={`f001-cam${i + 1}`}
                campos={CAMPOS_CAMBIO_001}
                valores={c}
                columnas={3}
                onChange={(v) => setDatos001((prev) => ({ ...prev, cambios: prev.cambios.map((x, j) => (j === i ? v : x)) }))}
              />
            </div>
          </details>
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f001.egresoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f001.egresoNota')}</p>
        {actual && yaTieneDiagnosticosDeEgreso(actual) && <p className="text-xs font-medium text-hce-ink">{t('formularios.f001.egresoYaRegistrado')}</p>}
        {datos001.ordenes.map((o, i) => (
          <details key={i} open={i === 0} className="rounded-md border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium text-hce-ink">{`${t('formularios.f001.ordenRotulo')} ${i + 1}`}</summary>
            <div className="mt-3 flex flex-col gap-3">
              <CamposSimples
                prefijoId={`f001-ord${i + 1}`}
                campos={CAMPOS_ORDEN_001}
                valores={o.valores}
                columnas={3}
                onChange={(v) => setDatos001((prev) => ({ ...prev, ordenes: prev.ordenes.map((x, j) => (j === i ? { ...x, valores: v } : x)) }))}
              />
              <SelectorDiagnosticoConMarca
                label={t('formularios.f001.diagnosticoPrincipal')}
                marcaLabel={t('formularios.f001.marcaDiagnostico')}
                marcas={MARCAS_001}
                value={o.principal}
                onChange={(v) => setDatos001((prev) => ({ ...prev, ordenes: prev.ordenes.map((x, j) => (j === i ? { ...x, principal: v } : x)) }))}
              />
              <SelectorDiagnosticoConMarca
                label={t('formularios.f001.diagnosticoSecundario')}
                marcaLabel={t('formularios.f001.marcaDiagnostico')}
                marcas={MARCAS_001}
                value={o.secundario}
                onChange={(v) => setDatos001((prev) => ({ ...prev, ordenes: prev.ordenes.map((x, j) => (j === i ? { ...x, secundario: v } : x)) }))}
              />
            </div>
          </details>
        ))}
      </Bloque>

      <ErrorGuardado error={errorGuardado} />

      {atencionCerrada && (
        <Alert variant="red">
          <AlertTitle>{t('formularios.f001.atencionCerradaTitulo')}</AlertTitle>
          <AlertDescription>{t('formularios.f001.atencionCerradaDetalle')}</AlertDescription>
        </Alert>
      )}

      <Button type="button" className="w-fit" disabled={guardando || atencionCerrada} onClick={() => void ejecutarGuardado(guardarEncounter)}>
        {t('formulario.guardar')}
      </Button>

      {fase.fase === 'guardado' && (
        <>
          <Alert>
            <AlertTitle>{t('formulario.guardadoTitulo')}</AlertTitle>
            <AlertDescription>{t('formulario.guardadoDetalle')}</AlertDescription>
          </Alert>

          {faltaEpicrisis && (
            <Alert variant="red">
              <AlertTitle>{t('formularios.f001.faltaEpicrisisTitulo')}</AlertTitle>
              <AlertDescription>
                {t('formularios.f001.faltaEpicrisisDetalle')}{' '}
                <Link
                  to={`/${id}/formulario/006?${new URLSearchParams({ encounter: encounter.reference ?? `Encounter/${id}` }).toString()}`}
                  className="font-medium underline underline-offset-2"
                >
                  {t('formularios.f001.faltaEpicrisisLink')}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {faltantesFiliacion.length > 0 && (
            <Alert variant="red">
              <AlertTitle>{t('formularios.f001.filiacionIncompletaTitulo')}</AlertTitle>
              <AlertDescription>
                {t('formularios.f001.filiacionIncompletaDetalle')}{' '}
                <Link to={`/${id}/datos-demograficos`} className="font-medium underline underline-offset-2">
                  {t('formularios.f001.filiacionIncompletaLink')}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          <PanelFirmaImpresion
            key={numGuardados}
            documentoId={encounter.reference ?? `Encounter/${id}`}
            tipoDocumento={CODIGO}
            titulo={t('formularios.f001.titulo')}
            subject={subject}
            bloqueadoExternamente={faltantesFiliacion.length > 0}
            onFirmado={async () => setFirmado(true)}
            especificacionBuilder={async () => {
              return { modo: 'msp', pares: paresGuardados, datosEncabezadoMsp };
            }}
          />
        </>
      )}
    </div>
  );
}
